import { useState, type FormEvent } from 'react';
import { PageShell } from '../components/PageShell';
import { SUPPORT_EMAIL } from '../content';
import { ArrowIcon } from '../icons';

type Topic = 'General' | 'Support' | 'Feedback' | 'Press' | 'Billing';

const TOPICS: { value: Topic; label: string }[] = [
  { value: 'General', label: 'General — say hello' },
  { value: 'Support', label: 'Support — something’s broken' },
  { value: 'Feedback', label: 'Feedback — a feature idea' },
  { value: 'Press', label: 'Press & partnerships' },
  { value: 'Billing', label: 'Billing & subscriptions' },
];

export function ContactPage() {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState<Topic>('General');
  const [msg, setMsg] = useState('');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const cleanName = name.trim().slice(0, 120);
    const cleanMsg = msg.trim().slice(0, 4000);
    const subject = `Cranio · ${topic}${cleanName ? ' — ' + cleanName : ''}`;
    const body = cleanMsg + (cleanName ? `\n\n— ${cleanName}` : '');
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  return (
    <PageShell>
      <div className="container">
        <div style={{ maxWidth: 640, marginBottom: 56 }}>
          <div className="eyebrow" style={{ marginBottom: 22 }}>Contact</div>
          <h1 className="h-display" style={{ fontSize: 'clamp(40px, 6vw, 76px)' }}>
            Let&apos;s talk.
          </h1>
          <p className="h-sub" style={{ marginTop: 22, fontSize: 19 }}>
            Questions, bug reports, feature ideas, press — it all lands in the same studio inbox,
            and a real person reads every message. We usually reply within one working day.
          </p>
        </div>

        <div className="contact-grid">
          <div>
            <div className="mail-card ticks grain">
              <span className="tick-br"></span>
              <div className="eyebrow" style={{ marginBottom: 16 }}>Write to us</div>
              <a
                className="mail-addr"
                href={`mailto:${SUPPORT_EMAIL}?subject=Hello%20Cranio`}
              >
                {SUPPORT_EMAIL}
                <span
                  style={{
                    display: 'inline-grid',
                    placeItems: 'center',
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    border: '0.5px solid var(--line-2)',
                    color: 'var(--fg-1)',
                  }}
                >
                  <ArrowIcon />
                </span>
              </a>
              <p
                style={{
                  color: 'var(--fg-3)',
                  fontSize: '13.5px',
                  margin: '18px 0 0',
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                Tap the address to open your mail app, or use the quick note below — we&apos;ll
                prefill the subject for you.
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              style={{
                marginTop: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }} className="contact-row">
                <div className="field">
                  <label htmlFor="contact-name">Your name</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    placeholder="Camille R."
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={120}
                  />
                </div>
                <div className="field">
                  <label htmlFor="contact-topic">Topic</label>
                  <select
                    id="contact-topic"
                    name="topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value as Topic)}
                  >
                    {TOPICS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="contact-msg">Message</label>
                <textarea
                  id="contact-msg"
                  name="msg"
                  rows={5}
                  placeholder="What's on your mind?"
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  maxLength={4000}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                <button type="submit" className="btn btn-solid" style={{ padding: '14px 22px' }}>
                  Open in email
                  <ArrowIcon />
                </button>
                <span
                  className="mono"
                  style={{ fontSize: 11, color: 'var(--fg-4)', letterSpacing: '0.04em' }}
                >
                  Opens your mail app — nothing is sent until you hit send.
                </span>
              </div>
            </form>
          </div>

          <aside style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="eyebrow" style={{ marginBottom: 2 }}>Direct lines</div>
            <a className="chan" href={`mailto:${SUPPORT_EMAIL}?subject=Cranio%20Support`}>
              <span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
                  Support
                </span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {SUPPORT_EMAIL}
                </span>
              </span>
              <span className="ck">01</span>
            </a>
            <a className="chan" href={`mailto:${SUPPORT_EMAIL}?subject=Cranio%20Feedback`}>
              <span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
                  Feedback
                </span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {SUPPORT_EMAIL}
                </span>
              </span>
              <span className="ck">02</span>
            </a>
            <a className="chan" href={`mailto:${SUPPORT_EMAIL}?subject=Cranio%20Press`}>
              <span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
                  Press &amp; partners
                </span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {SUPPORT_EMAIL}
                </span>
              </span>
              <span className="ck">03</span>
            </a>

            <div
              style={{
                marginTop: 22,
                padding: 24,
                border: '0.5px solid var(--line-1)',
                borderRadius: 6,
                background: 'var(--bg-1)',
              }}
            >
              <div className="eyebrow" style={{ marginBottom: 14 }}>The studio</div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--fg-2)', margin: 0 }}>
                Cranio is a small independent studio building tools for artists.
                <br />
                <span className="mono" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                  Reply time · within 1 working day
                </span>
                <br />
                <span className="mono" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                  Hours · Mon–Fri, 9–18 CET
                </span>
              </p>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}
